import { useState } from "react";
import { Check, Cloud, FolderTree, HardDrive, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  createDestination,
  createFolderTree,
  updateDestination,
  type DestinationType,
} from "@/lib/integrations/destinations";
import {
  FOLDER_LAYOUTS,
  DEFAULT_LAYOUT,
  safeFolderName,
  type FolderLayoutKey,
} from "@/lib/integrations/folderTree";

interface StorageSetupStepProps {
  projectId: string;
  projectName: string;
  /** Anropas när användaren är klar (även vid "Hoppa över"). */
  onDone: () => void;
  doneLabel?: string;
}

const TYPES: Array<{ key: DestinationType; label: string; hint: string; icon: typeof Cloud }> = [
  { key: "sharepoint", label: "SharePoint", hint: "Dokumentbibliotek i Microsoft 365", icon: Cloud },
  { key: "onedrive", label: "OneDrive", hint: "Personlig eller delad OneDrive", icon: HardDrive },
];

export function StorageSetupStep({
  projectId,
  projectName,
  onDone,
  doneLabel = "Klar",
}: StorageSetupStepProps) {
  const [type, setType] = useState<DestinationType | null>(null);
  const [layoutKey, setLayoutKey] = useState<FolderLayoutKey>(DEFAULT_LAYOUT);
  const [rootName, setRootName] = useState(safeFolderName(projectName));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: string[]; error?: string } | null>(null);

  const layout = FOLDER_LAYOUTS[layoutKey];

  const handleCreate = async () => {
    if (!type) return;
    setBusy(true);
    setResult(null);
    const dest = await createDestination({ projectId, connectorType: type, label: rootName });
    if (!dest) {
      setBusy(false);
      toast.error("Kunde inte spara lagringsplatsen");
      return;
    }
    // Spara valt upplägg direkt så att kommande exporter hamnar rätt även om
    // Microsoft-kopplingen ansluts först senare.
    await updateDestination(dest.id, {
      folder_layout: { key: layoutKey, folders: layout.folders.map((f) => f.path) },
      root_path: safeFolderName(rootName),
    });

    const tree = await createFolderTree({
      destinationId: dest.id,
      layout: layoutKey,
      basePath: rootName,
    });
    setBusy(false);
    setResult({ created: tree.created, error: tree.error });
    if (tree.ok) toast.success("Mappträdet har skapats");
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-medium">Var ska dokumenten sparas?</h3>
        <p className="text-sm text-muted-foreground">
          Arbeta i Byggarbetsmiljökontrollen och låt dokumenten landa i ert eget system. Vi skapar ett
          mappträd så att hela förloppet är lätt att följa.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {TYPES.map((t) => {
          const Icon = t.icon;
          const active = type === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setType(active ? null : t.key)}
              className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${
                active ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              }`}
            >
              <Icon className="mt-0.5 h-5 w-5 text-muted-foreground" />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{t.label}</span>
                <span className="block text-xs text-muted-foreground">{t.hint}</span>
              </span>
              {active && <Check className="ml-auto h-4 w-4 text-primary" />}
            </button>
          );
        })}
      </div>

      {type && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="root-folder">Projektets rotmapp</Label>
            <Input
              id="root-folder"
              value={rootName}
              onChange={(e) => setRootName(e.target.value)}
              placeholder="Projektnamn"
            />
          </div>

          <div className="space-y-2">
            <Label>Mappstruktur</Label>
            <div className="grid gap-2">
              {Object.values(FOLDER_LAYOUTS).map((l) => (
                <button
                  key={l.key}
                  type="button"
                  onClick={() => setLayoutKey(l.key)}
                  className={`rounded-xl border p-3 text-left transition ${
                    layoutKey === l.key ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <FolderTree className="h-4 w-4 text-muted-foreground" />
                    {l.title}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">{l.description}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border bg-muted/30 p-3">
            <p className="text-xs font-medium text-muted-foreground">Så här blir mapparna</p>
            <ul className="mt-2 space-y-1 font-mono text-xs">
              <li>{safeFolderName(rootName) || "Projekt"}/</li>
              {layout.folders.map((f) => (
                <li key={f.path} className="text-muted-foreground">
                  {"  "}
                  {f.path}
                  {f.hint ? <span className="font-sans"> · {f.hint}</span> : null}
                </li>
              ))}
            </ul>
          </div>

          {result?.error && (
            <Alert>
              <AlertTitle>Mappträdet kunde inte skapas ännu</AlertTitle>
              <AlertDescription className="text-sm">
                {result.error} Valet är sparat – anslut Microsoft under Integrationer och skapa
                mappträdet därifrån.
              </AlertDescription>
            </Alert>
          )}

          {result && !result.error && (
            <Alert>
              <AlertTitle className="flex items-center gap-2">
                <Check className="h-4 w-4" /> Klart
              </AlertTitle>
              <AlertDescription className="text-sm">
                {result.created.length} mappar skapades i {type === "sharepoint" ? "SharePoint" : "OneDrive"}.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {type && !result && (
          <Badge variant="secondary" className="mr-auto">
            {layout.folders.length} mappar
          </Badge>
        )}
        <Button type="button" variant="outline" onClick={onDone} disabled={busy}>
          {result ? doneLabel : "Hoppa över"}
        </Button>
        {type && !result && (
          <Button type="button" onClick={handleCreate} disabled={busy || !rootName.trim()}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FolderTree className="mr-2 h-4 w-4" />}
            Skapa mappträd
          </Button>
        )}
      </div>
    </div>
  );
}

export default StorageSetupStep;
